import { useGetInvoicesStats, useGetInvoicesStatsExcel } from "@/api/invoice";
import StyledSpinner from "@/components/styled/StyledSpinner";
import { validateInvoiceStatus } from "@/types/invoiceStatus";
import { DataTable } from "@/components/ui/customs/data-table/data-table";
import { useInvoiceStatsColumns } from "./invoiceStatsColumn";
import { useTranslation } from "react-i18next";
import { useState, useCallback } from "react";
import { ColumnFiltersState, PaginationState, Row, SortingState } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { FileDown } from "lucide-react";
import { useDownloadInvoiceStatsExcel } from "@/hooks/useDownloadInvoiceStatsExcel";
import { GetInvoiceStatsResponseDto } from "@/types/api/api-types";

type InvoiceStatsProps = {
  year?: number;
  month?: number[];
  paymentStatus?: string;
  businessName?: string;
  invoiceId?: string;
};

export const InvoiceStats = ({
  year,
  month,
  paymentStatus,
  businessName,
  invoiceId,
}: InvoiceStatsProps) => {
  const { t } = useTranslation("boInvoices");
  const invoiceStatsColumns = useInvoiceStatsColumns();
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 1000,
  });
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const monthToSend = month?.map((m) => m);
  const { data: invoiceStats } = useGetInvoicesStats({
    year,
    month: monthToSend ? monthToSend : undefined,
    paymentStatus: paymentStatus ? validateInvoiceStatus(paymentStatus) : undefined,
    businessName,
    invoiceId,
  });

  const { data: invoiceStatsExcel } = useGetInvoicesStatsExcel({
    year,
    month: monthToSend ? monthToSend : undefined,
    paymentStatus: paymentStatus ? validateInvoiceStatus(paymentStatus) : undefined,
  });

  const downloadExcel = useDownloadInvoiceStatsExcel(invoiceStatsExcel);

  type CompanyStats = GetInvoiceStatsResponseDto["companies"][0];

  const getRowCanExpand = useCallback(
    (row: Row<CompanyStats>) =>
      "billingEntities" in row.original &&
      Array.isArray(row.original.billingEntities) &&
      row.original.billingEntities.length > 0,
    [],
  );

  if (!invoiceStats) {
    return (
      <div className="flex h-full items-center justify-center">
        <StyledSpinner />
      </div>
    );
  }

  return (
    <div className="flex flex-col items-start justify-center gap-4 p-4">
      <div className="flex flex-row items-center gap-2">
        <h2 className="text-xl font-bold">{t("stats.title")}</h2>
        <Button variant="outline" onClick={downloadExcel}>
          <FileDown />
        </Button>
      </div>
      <p className="font-bold text-gray-600">
        {t("stats.totalExcludingTax", {
          amount: (invoiceStats.total / 100).toLocaleString("fr-FR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }),
        })}
      </p>
      <div className="w-full">
        <DataTable
          columns={invoiceStatsColumns}
          data={invoiceStats.companies}
          pagination={pagination}
          setPagination={setPagination}
          sorting={sorting}
          setSorting={setSorting}
          columnFilters={columnFilters}
          setColumnFilters={setColumnFilters}
          getRowCanExpand={getRowCanExpand}
        />
      </div>
    </div>
  );
};
